import APIService from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { FormatCurrency } from "@/common/helpers"
import OrderTracePanel from "@/components/Documents/OrderTracePanel"
import { AgentInvoiceDetailItem, AgentInvoiceMonth, AgentInvoicesByMonth as AgentInvoicesByMonthDTO } from "@/types/Agents.types"
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from "chart.js"
import moment from "moment"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Bar } from "react-chartjs-2"
import { Badge, Card, Form, Modal, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useHistory } from "react-router-dom"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

interface Props {
    agentId: string
}

type PaidFilter = "all" | "paid" | "unpaid"

const Variance = ({ value }: { value?: number | null }) => {
    if (value === null || value === undefined) return <span className="text-muted">—</span>

    const isUp = value >= 0
    return (
        <span className={`fw-semibold ${isUp ? "text-success" : "text-danger"}`}>
            <i className={`fa fa-arrow-${isUp ? "up" : "down"} me-1`}></i>
            {Math.abs(value)}%
        </span>
    )
}

// Vertical rule between the two year blocks and before the Variance block — makes the
// three-way split (this year / last year / variance) legible at a glance.
const DIVIDER_STYLE = { borderLeft: "2px solid #dee2e6" }

const AgentInvoicesByMonth = ({ agentId }: Props) => {
    const { t } = useTranslation()
    const history = useHistory()
    const user = parsedUser()

    const [data, setData] = useState<AgentInvoicesByMonthDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [year, setYear] = useState<number>(new Date().getFullYear())

    // Detail modal — "what happened" drill-down for a clicked month
    const [detailMonth, setDetailMonth] = useState<AgentInvoiceMonth | null>(null)
    const [detailYear, setDetailYear] = useState<number>(year)
    const [detailRecords, setDetailRecords] = useState<AgentInvoiceDetailItem[]>([])
    const [detailLoading, setDetailLoading] = useState(false)
    const [detailSearch, setDetailSearch] = useState("")
    const [detailPaidFilter, setDetailPaidFilter] = useState<PaidFilter>("all")

    // Reused document trace panel — same component the merchandise search page uses to
    // resolve an invoice number into its related trip/dispatch documents.
    const [traceOrder, setTraceOrder] = useState("")

    const bindData = useCallback(async (selectedYear: number) => {
        if (!user?.OrganizationId || !agentId) return

        try {
            setLoading(true)
            const request = await APIService.getAction(
                "agents/InvoicesByMonth",
                `OrganizationId=${user.OrganizationId}&Id=${agentId}&Year=${selectedYear}`
            )
            if (request.data.status === "ok") {
                setData(request.data.response)
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setLoading(false)
        }
    }, [user?.OrganizationId, agentId])

    useEffect(() => {
        bindData(year)
    }, [bindData, year])

    const loadDetail = async (targetYear: number, month: number) => {
        if (!user?.OrganizationId || !agentId) return
        try {
            setDetailLoading(true)
            setDetailRecords([])
            const request = await APIService.getAction(
                "agents/InvoiceMonthDetail",
                `OrganizationId=${user.OrganizationId}&Id=${agentId}&Year=${targetYear}&Month=${month}`
            )
            if (request.data.status === "ok") {
                setDetailRecords(request.data.response || [])
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setDetailLoading(false)
        }
    }

    const openDetail = (row: AgentInvoiceMonth, targetYear: number) => {
        setDetailMonth(row)
        setDetailYear(targetYear)
        setDetailSearch("")
        setDetailPaidFilter("all")
        loadDetail(targetYear, row.month)
    }

    const closeDetail = () => {
        setDetailMonth(null)
        setDetailRecords([])
    }

    const goToClientSearch = (inv: AgentInvoiceDetailItem) => {
        const term = inv.clientName || inv.clientCode || ""
        if (!term) return
        history.push(`/search?search=${encodeURIComponent(term)}`)
    }

    const filteredDetailRecords = useMemo(() => {
        return detailRecords.filter(inv => {
            if (detailPaidFilter === "paid" && inv.paid !== "SI") return false
            if (detailPaidFilter === "unpaid" && inv.paid === "SI") return false
            if (detailSearch.trim()) {
                const needle = detailSearch.trim().toLowerCase()
                const haystack = `${inv.invoiceNumber} ${inv.clientName ?? ""} ${inv.clientCode ?? ""}`.toLowerCase()
                if (!haystack.includes(needle)) return false
            }
            return true
        })
    }, [detailRecords, detailSearch, detailPaidFilter])

    const chartData = useMemo(() => {
        if (!data) return null
        return {
            labels: data.months.map(m => m.monthLabel),
            datasets: [
                {
                    label: String(data.year),
                    data: data.months.map(m => m.currentAmount),
                    backgroundColor: "rgba(59, 130, 246, 0.75)",
                    borderRadius: 4,
                },
                {
                    label: String(data.previousYear),
                    data: data.months.map(m => m.previousAmount),
                    backgroundColor: "rgba(148, 163, 184, 0.6)",
                    borderRadius: 4,
                },
            ],
        }
    }, [data])

    const renderRow = (row: AgentInvoiceMonth, isTotal = false) => (
        <tr
            key={isTotal ? "total" : row.month}
            className={isTotal ? "table-light fw-semibold" : "clickable"}
            onClick={isTotal ? undefined : () => openDetail(row, data!.year)}
            title={isTotal ? undefined : (t("Click to see the invoices for this month") as string)}
        >
            <td className="ps-3">{isTotal ? t("Total") : row.monthLabel}</td>
            <td className="text-center">{row.currentCount}</td>
            <td className="text-end">{FormatCurrency(row.currentAmount)}</td>
            <td
                className="text-center text-muted"
                style={DIVIDER_STYLE}
                onClick={isTotal ? undefined : (e) => { e.stopPropagation(); openDetail(row, data!.previousYear) }}
            >
                {row.previousCount}
            </td>
            <td
                className="text-end text-muted"
                onClick={isTotal ? undefined : (e) => { e.stopPropagation(); openDetail(row, data!.previousYear) }}
            >
                {FormatCurrency(row.previousAmount)}
            </td>
            <td className={`text-end ${row.amountVariance >= 0 ? "text-success" : "text-danger"}`} style={DIVIDER_STYLE}>
                {row.amountVariance >= 0 ? "+" : ""}
                {FormatCurrency(row.amountVariance)}
            </td>
            <td className="text-end pe-3">
                <Variance value={row.amountVariancePercent} />
            </td>
        </tr>
    )

    const detailTotal = filteredDetailRecords.reduce((sum, r) => sum + r.amount, 0)

    return (
        <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h6 className="mb-0">
                    <i className="fa fa-file-invoice-dollar me-2 text-primary" />
                    {t("Invoices by Month")}
                    <small className="text-muted ms-2">
                        {year} {t("vs")} {year - 1}
                    </small>
                </h6>
                <Form.Select
                    size="sm"
                    style={{ width: "auto" }}
                    value={year}
                    onChange={e => setYear(Number(e.target.value))}
                >
                    {(data?.availableYears?.length ? data.availableYears : [year]).map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </Form.Select>
            </Card.Header>
            <Card.Body className="p-0">
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" variant="primary" size="sm" />
                    </div>
                ) : !data ? (
                    <div className="text-center text-muted py-4">{t("No records found")}</div>
                ) : (
                    <>
                        {chartData && (
                            <div className="p-3 border-bottom" style={{ height: 240 }}>
                                <Bar
                                    data={chartData}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        plugins: {
                                            legend: { position: "top" as const },
                                            tooltip: {
                                                callbacks: {
                                                    label: (ctx) => `${ctx.dataset.label}: ${FormatCurrency(Number(ctx.parsed.y))}`,
                                                },
                                            },
                                        },
                                        scales: {
                                            y: {
                                                beginAtZero: true,
                                                ticks: { callback: (value) => FormatCurrency(Number(value)) },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        )}
                        <div className="table-responsive">
                            <Table hover className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3" rowSpan={2}>{t("Month")}</th>
                                        <th className="text-center" colSpan={2}>{data.year}</th>
                                        <th className="text-center" colSpan={2} style={DIVIDER_STYLE}>{data.previousYear}</th>
                                        <th className="text-end" colSpan={2} style={DIVIDER_STYLE}>{t("Variance")}</th>
                                    </tr>
                                    <tr>
                                        <th className="text-center small">{t("Qty")}</th>
                                        <th className="text-end small">{t("Amount")}</th>
                                        <th className="text-center small" style={DIVIDER_STYLE}>{t("Qty")}</th>
                                        <th className="text-end small">{t("Amount")}</th>
                                        <th className="text-end small" style={DIVIDER_STYLE}>{t("Amount")}</th>
                                        <th className="text-end pe-3 small">%</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.months.map(row => renderRow(row))}
                                    {renderRow(data.total, true)}
                                </tbody>
                            </Table>
                        </div>
                        <div className="text-muted small px-3 py-2 border-top">
                            <i className="fa fa-circle-info me-1" />
                            {t("Click a month to see the invoices behind that number")}
                        </div>
                    </>
                )}
            </Card.Body>

            <Modal show={detailMonth !== null} onHide={closeDetail} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {detailMonth && `${detailMonth.monthLabel} ${detailYear}`}
                        {detailMonth && (
                            <Badge bg="light" text="dark" className="ms-2">
                                {filteredDetailRecords.length} {t("Invoices")}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <div className="d-flex flex-wrap gap-2 align-items-center px-3 py-2 border-bottom bg-light">
                        <Form.Control
                            size="sm"
                            style={{ maxWidth: 240 }}
                            placeholder={t("Search invoice or client") as string}
                            value={detailSearch}
                            onChange={e => setDetailSearch(e.target.value)}
                        />
                        <Form.Select
                            size="sm"
                            style={{ width: "auto" }}
                            value={detailPaidFilter}
                            onChange={e => setDetailPaidFilter(e.target.value as PaidFilter)}
                        >
                            <option value="all">{t("All")}</option>
                            <option value="paid">{t("Paid")}</option>
                            <option value="unpaid">{t("Unpaid")}</option>
                        </Form.Select>
                    </div>
                    {detailLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" size="sm" />
                        </div>
                    ) : filteredDetailRecords.length === 0 ? (
                        <div className="text-center text-muted py-5">{t("No records found")}</div>
                    ) : (
                        <div className="table-responsive" style={{ maxHeight: 440, overflowY: "auto" }}>
                            <Table hover size="sm" className="mb-0 align-middle">
                                <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                                    <tr>
                                        <th className="ps-3">{t("Invoice")}</th>
                                        <th>{t("Date")}</th>
                                        <th>{t("Client")}</th>
                                        <th>{t("Status")}</th>
                                        <th className="text-end pe-3">{t("Amount")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDetailRecords.map(inv => (
                                        <tr key={inv.invoiceNumber}>
                                            <td className="ps-3">
                                                <span
                                                    className="clickable text-primary"
                                                    onClick={() => setTraceOrder(inv.invoiceNumber)}
                                                    title={t("View related documents") as string}
                                                >
                                                    {inv.invoiceNumber}
                                                </span>
                                            </td>
                                            <td className="text-nowrap">{moment(inv.date).format("DD/MM/YYYY")}</td>
                                            <td>
                                                <span className="clickable" onClick={() => goToClientSearch(inv)}>
                                                    <div>{inv.clientName}</div>
                                                    {inv.clientCode && <small className="text-muted">{inv.clientCode}</small>}
                                                </span>
                                            </td>
                                            <td>
                                                <Badge bg={inv.paid === "SI" ? "success" : "warning"} text={inv.paid === "SI" ? undefined : "dark"}>
                                                    {inv.invoiceStatus || inv.paid || "—"}
                                                </Badge>
                                            </td>
                                            <td className="text-end pe-3">{FormatCurrency(inv.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="table-light fw-semibold">
                                        <td colSpan={4} className="ps-3 text-end">{t("Total")}</td>
                                        <td className="text-end pe-3">{FormatCurrency(detailTotal)}</td>
                                    </tr>
                                </tfoot>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <OrderTracePanel
                show={!!traceOrder}
                orderNumber={traceOrder}
                organizationId={String(user?.OrganizationId || "")}
                onHide={() => setTraceOrder("")}
            />
        </Card>
    )
}

export default AgentInvoicesByMonth
